import { Fragment } from "react";

import type { KpiSummary, PerCornerKpi } from "../api/runs";
import type { Run } from "../api/tests";
import { exportTableCsv, type CsvCell } from "./chartExport";

export type KpiRowState =
  | { kind: "loading" }
  | { kind: "ready"; data: KpiSummary }
  | { kind: "error"; message: string };

export type KpiRow = {
  run: Run;
  kpi: KpiRowState;
  intent?: string;
};

export type KpiComparisonTableProps = {
  rows: KpiRow[];
  r0RunId: number | null;
  deltasAvailable: boolean;
  onRetry: (runId: number, kind: "kpi" | "waveform") => void;
};

const CORNERS = ["LF", "RF", "LR", "RR"] as const;
type Metric = "r2_dyn" | "rmse_mm" | "dmean_mm";

// Null for track-replay runs (item D): r2_dyn/rmse have no ground truth, and
// dmean needs recorded_mean_m. predicted_mean_m is always present.
function metricValue(per: PerCornerKpi, m: Metric): number | null {
  switch (m) {
    case "r2_dyn": return per.r2_dyn;
    case "rmse_mm": return per.rmse_m === null ? null : per.rmse_m * 1000;
    case "dmean_mm":
      return per.recorded_mean_m === null
        ? null : (per.predicted_mean_m - per.recorded_mean_m) * 1000;
  }
}

function fmt(value: number | null, m: Metric): string {
  if (value === null) return "—";
  switch (m) {
    case "r2_dyn": return value.toFixed(3);
    case "rmse_mm": return `${value.toFixed(1)} mm`;
    case "dmean_mm": {
      const signed = value >= 0 ? `+${value.toFixed(1)}` : value.toFixed(1);
      return `${signed} mm`;
    }
  }
}

function fmtDelta(delta: number, m: Metric): string {
  const digits = m === "r2_dyn" ? 3 : 1;
  return delta >= 0 ? `+${delta.toFixed(digits)}` : delta.toFixed(digits);
}

function rowIntent(row: KpiRow): string | null {
  if (row.intent && row.intent.length > 0) return row.intent;
  return null;
}

function StickyRunCell({ label, intent }: { label: string; intent: string | null }) {
  return (
    <td className="sticky-col">
      <div>{label}</div>
      {intent != null ? <div className="muted">{intent}</div> : null}
    </td>
  );
}

function perCornerByName(s: KpiSummary, corner: string): PerCornerKpi | null {
  const key = corner.toLowerCase();
  return s.per_corner.find((p) => p.corner.toLowerCase() === key) ?? null;
}

function sortRows(rows: KpiRow[], r0RunId: number | null): KpiRow[] {
  return [...rows].sort((a, b) => {
    if (r0RunId != null) {
      if (a.run.id === r0RunId) return -1;
      if (b.run.id === r0RunId) return 1;
    }
    return a.run.run_number - b.run.run_number;
  });
}

// Serialise the settled rows to a CSV grid: one header, one row per ready run,
// three columns (R²_dyn / RMSE mm / Δmean mm) per corner.
export function buildKpiCsvRows(
  rows: KpiRow[],
  r0RunId: number | null,
): CsvCell[][] {
  const header: CsvCell[] = ["Run", "Intent"];
  for (const c of CORNERS) {
    header.push(`${c} R2_dyn`, `${c} RMSE_mm`, `${c} dmean_mm`);
  }
  const out: CsvCell[][] = [header];
  for (const row of sortRows(rows, r0RunId)) {
    if (row.kpi.kind !== "ready") continue;
    const label = row.run.id === r0RunId ? "R0" : `R${row.run.run_number}`;
    const cells: CsvCell[] = [label, rowIntent(row) ?? ""];
    for (const c of CORNERS) {
      const per = perCornerByName(row.kpi.data, c);
      if (per == null) {
        cells.push("", "", "");
        continue;
      }
      cells.push(
        metricValue(per, "r2_dyn"),
        metricValue(per, "rmse_mm"),
        metricValue(per, "dmean_mm"),
      );
    }
    out.push(cells);
  }
  return out;
}

export function KpiComparisonTable({
  rows, r0RunId, deltasAvailable, onRetry,
}: KpiComparisonTableProps) {
  const sorted = sortRows(rows, r0RunId);
  const r0 = sorted.find((r) => r.run.id === r0RunId);
  const r0Kpi = r0 && r0.kpi.kind === "ready" ? r0.kpi.data : null;
  const hasReadyRow = sorted.some((r) => r.kpi.kind === "ready");

  return (
    <div className="kpi-comparison-wrap">
    <div className="kpi-comparison-chrome">
      <button
        type="button"
        className="chart-export-btn"
        data-testid="kpi-export-csv"
        title="Export the KPI table as CSV"
        aria-label="Export the KPI table as CSV"
        disabled={!hasReadyRow}
        onClick={() => exportTableCsv(buildKpiCsvRows(rows, r0RunId), "kpi_comparison.csv")}
      >
        CSV
      </button>
    </div>
    <table className="kpi-comparison-table">
      <thead>
        <tr>
          <th rowSpan={2} className="sticky-col">Run</th>
          {CORNERS.map((c) => (
            <th key={c} colSpan={3} className="corner-group">
              {c}
            </th>
          ))}
        </tr>
        <tr>
          {CORNERS.map((c) => (
            <Fragment key={c}>
              <th>R²_dyn</th>
              <th>RMSE</th>
              <th>Δmean</th>
            </Fragment>
          ))}
        </tr>
      </thead>
      <tbody>
        {sorted.map((row) => {
          const label = row.run.id === r0RunId ? "R0" : `R${row.run.run_number}`;
          const intent = rowIntent(row);
          if (row.kpi.kind === "error") {
            return (
              <tr key={row.run.id} data-testid={`kpi-row-${row.run.id}`}>
                <StickyRunCell label={label} intent={intent} />
                <td colSpan={CORNERS.length * 3} className="row-error">
                  <span className="error-chip">{row.kpi.message}</span>
                  <button
                    type="button"
                    className="retry-btn"
                    onClick={() => onRetry(row.run.id, "kpi")}
                  >
                    Retry
                  </button>
                </td>
              </tr>
            );
          }
          if (row.kpi.kind === "loading") {
            return (
              <tr key={row.run.id} data-testid={`kpi-row-${row.run.id}`}>
                <StickyRunCell label={label} intent={intent} />
                <td colSpan={CORNERS.length * 3} className="row-loading">
                  Loading…
                </td>
              </tr>
            );
          }
          const kpiData = row.kpi.data;
          return (
            <tr key={row.run.id} data-testid={`kpi-row-${row.run.id}`}>
              <StickyRunCell label={label} intent={intent} />
              {CORNERS.flatMap((c) => {
                const per = perCornerByName(kpiData, c);
                if (per == null) {
                  return ["r2", "rmse", "d"].map((slot) => (
                    <td key={`${c}-${slot}-missing`}>—</td>
                  ));
                }
                return (["r2_dyn", "rmse_mm", "dmean_mm"] as Metric[]).map(
                  (m) => {
                    const v = metricValue(per, m);
                    const display = fmt(v, m);
                    const r0Per = r0Kpi ? perCornerByName(r0Kpi, c) : null;
                    // Delta only when both runs have the metric (replay runs
                    // have null r2_dyn/rmse/dmean -> no delta shown).
                    const r0v =
                      deltasAvailable && r0Per != null && row.run.id !== r0RunId
                        ? metricValue(r0Per, m) : null;
                    const delta = v !== null && r0v !== null ? v - r0v : null;
                    return (
                      <td key={`${c}-${m}`}>
                        <span className="cell-primary">{display}</span>
                        {delta != null ? (
                          <span
                            className="cell-delta muted"
                            data-testid={`delta-vs-r0-${m}`}
                          >
                            {" "}
                            ({fmtDelta(delta, m)})
                          </span>
                        ) : null}
                      </td>
                    );
                  },
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
    </div>
  );
}
